import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, Subject } from 'rxjs';

import { Antwoord, ClaimOverview, IndienenVragenboomResponse, ProductInfo, Referentiegegeven, Vraag, Vragenboom, VragenboomResponse } from '@models/claims';
import { Verzekering, Meetgegevens, SchademeldenState, Contact, VraagboomAntwoord, IBaseVraagboomResultaat } from '@models/vragenboom';
import { Proces } from '../models/imports/generic';

@Injectable()
export class DataService {
  private _claimnummer: string;
  private _verzekering: Verzekering;
  private _meetgegevens: Meetgegevens;
  private _claimoverview: ClaimOverview;
  private _contactGegevens: Contact;
  private _vragenboom: Vragenboom;
  private _vragenboomResponse: IndienenVragenboomResponse;
  private _schadeindienenBoom: Boolean;
  private _sleutel: string;
  private _proces: Proces;
  private _omschrijvingLineOfBusiness: string;
  private _product: string;
  private _huidigeStap = 10;
  private _referentiegegevens;

  private _resultaten: Subject<VraagboomAntwoord<IBaseVraagboomResultaat>[]>
    = new BehaviorSubject<VraagboomAntwoord<IBaseVraagboomResultaat>[]>([]);
  public readonly resultaten: Observable<VraagboomAntwoord<IBaseVraagboomResultaat>[]> = this._resultaten.asObservable();

  private _productInfo: Subject<ProductInfo> = new BehaviorSubject<ProductInfo>(new ProductInfo);
  public readonly productInfo: Observable<ProductInfo> = this._productInfo.asObservable();

  private _submitClicked: Subject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly submitClicked: Observable<boolean> = this._submitClicked.asObservable();

  forceNavigation = false;

  getVerzekering(): Verzekering {
    return this._verzekering;
  }

  saveVerzekering(polisnummer: string, contractnummer: string, sleutel: string, kenmerk_titel: string, kenmerken: string[]) {
    this._verzekering = new Verzekering();
    this._verzekering.contractnummer = contractnummer;
    this._verzekering.polisnummer = polisnummer;
    this._verzekering.sleutel = sleutel;
    this._verzekering.kenmerk_titel = kenmerk_titel;
    this._verzekering.kenmerken = kenmerken;
  }

  getClaimnummer(): string {
    return this._claimnummer;
  }

  saveClaimnummer(claimnr: string) {
    this._claimnummer = claimnr;
  }

  getMeetgegevens(): Meetgegevens {
    return this._meetgegevens;
  }

  saveMeetGegevens(status: SchademeldenState, sleutel: string, reeks: Vraag[]) {
    if (this._meetgegevens == null) {
      this._meetgegevens = new Meetgegevens();
    }
    this._meetgegevens.reeks = reeks;
    this._meetgegevens.sleutel = sleutel;
    if (status !== undefined) {
      this.saveMeetGegevensStatus(status);
    }
  }

  saveMeetGegevensStatus(status: SchademeldenState) {
    if (this._meetgegevens == null) {
      this._meetgegevens = new Meetgegevens();
    }
    if (status === SchademeldenState.Verzekeringkiezer) {
      this._meetgegevens.status = 'kiezer-';
    } else if (status === SchademeldenState.Afronden) {
      this._meetgegevens.status = 'afronden-';
    } else {
      this._meetgegevens.status = 'indienen-';
    }
  }

  getSleutel(): string {
    return this._sleutel;
  }

  getVragenboom(): Vragenboom {
    return this._vragenboom;
  }

  isVragenboomKiezerBoom(): boolean {
    return !this._schadeindienenBoom && !!this._vragenboom;
  }

  isVragenboomSchadeindienenBoom(sleutel: string): boolean {
    return this._schadeindienenBoom && this._sleutel === sleutel && !!this._vragenboom;
  }

  getClaimoverview(): ClaimOverview {
    return this._claimoverview;
  }

  saveClaimoverview(claimoverview: ClaimOverview): void {
    this._claimoverview = claimoverview;
  }

  saveVragenboom(vragenboom: Vragenboom, schadeindienenBoom: Boolean, sleutel: string) {
    this._vragenboom = vragenboom;
    this._schadeindienenBoom = schadeindienenBoom;
    this._sleutel = sleutel;
  }

  getVragenboomResponse(): IndienenVragenboomResponse {
    return this._vragenboomResponse;
  }

  saveVragenboomResponse(vragenboomResponse: IndienenVragenboomResponse) {
    this._vragenboomResponse = vragenboomResponse;
  }

  getContact(): Contact {
    return this._contactGegevens;
  }

  saveContactgegevens(contactgegevens: Contact) {
    this._contactGegevens = contactgegevens;
  }

  bepaalHuidigeStap(vraag: Vraag): number {
    if (vraag == null) {
      this._huidigeStap = 10;
    }
    if (!vraag || !vraag.huidige_stap || Number(vraag.huidige_stap) <= this._huidigeStap) {
      return this._huidigeStap;
    }
    this._huidigeStap = Number(vraag.huidige_stap);
    return this._huidigeStap;
  }

  getProces(): Proces {
    return this._proces;
  }

  saveProces(proces: Proces) {
    this._proces = proces;
  }

  getPaza(): string {
    if (this._proces === Proces.SchademeldenZakelijk || this._proces === Proces.SchademeldenZakelijkAnoniem) {
      return 'be';
    }
    return 'pa';
  }

  getMeetidPazaProces(): string {
    if (this._proces === Proces.SchademeldenZakelijk || this._proces === Proces.SchademeldenZakelijkAnoniem) {
      return 'be-schademelden';
    }
    return 'pa-' + this.getProces();
  }

  getOmschrijvingLineOfBusiness(): string {
    return this._omschrijvingLineOfBusiness;
  }

  saveOmschrijvingLineOfBusiness(lob: string): void {
    this._omschrijvingLineOfBusiness = lob;
  }

  getProductOrOmschrijvingLineOfBusiness(): string {
    return this._product ? this._product : this._omschrijvingLineOfBusiness;
  }

  saveProduct(product: string): void {
    this._product = product;
  }

  saveResultaten(resultaten: VraagboomAntwoord<IBaseVraagboomResultaat>[]) {
    if (resultaten == null) {
      this._resultaten = null;
    } else {
      // overbodige antwoorden verwijderen
      const newReeks: VraagboomAntwoord<IBaseVraagboomResultaat>[] = [];
      let currentAntwoord: VraagboomAntwoord<IBaseVraagboomResultaat> = resultaten.find(v => v.vraag.startvraag);

      while (currentAntwoord != null && !currentAntwoord.vraag.eindvraag) {
        newReeks.push(currentAntwoord);
        currentAntwoord = resultaten.find(v => v.vraag.id === currentAntwoord.calculateNextVraagId(resultaten));
      }
      if (currentAntwoord != null) {
        newReeks.push(currentAntwoord);
      }

      this._resultaten.next(newReeks);
    }
  }

  getStandaardwaardeTekst(antwoord: Antwoord): string {
    const defaultResult = '';
    const standaardWaarde = antwoord?.standaard_waarde;
    if (!standaardWaarde)
      return defaultResult;

    if (this._contactGegevens) {
      const contactMatches = standaardWaarde.match(/^\{Relatie\.([a-zA-Z_][a-zA-Z_0-9]*)\}$/);
      if (contactMatches?.length === 2)
        return this._contactGegevens.getPropertyValue(contactMatches[1]);
    }

    return defaultResult;
  }

  saveProductInfo(info: ProductInfo) {
    this._productInfo.next(info);
  }

  setSubmitClicked(clicked: boolean) {
    this._submitClicked.next(clicked);
  }

  getReferentieGegevens(): Referentiegegeven[] {
    return this._referentiegegevens;
  }

  saveReferentieGegevens(response: VragenboomResponse): VragenboomResponse {
    this._referentiegegevens = response.referentiegegevens;
    return response;
  }
}
